import {Directive, ElementRef, Input, OnChanges, OnInit, SecurityContext} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import * as _ from 'lodash-es';
import {BehaviorSubject, combineLatest, defer, Observable, Subject} from 'rxjs';
import {distinctUntilChanged, map, share, startWith, takeUntil, tap} from 'rxjs/operators';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {destroy$} from '../destroyable';
import {DynamicComponentRef} from '../dynamic-component';
import {TooltipBalloonComponent} from './tooltip-balloon';
import {TooltipService} from './tooltip.service';

@Directive({
    selector: '[tooltip]',
    providers: [TooltipService],
    exportAs: 'tooltip',
    standalone: false
})
export class TooltipDirective implements OnInit, OnChanges {

    @Input()
    public tooltip: string | SafeHtml;

    /**
     * Need for providing state of textHiding directive. True by default.
     */
    public isEnabled$ = new BehaviorSubject<boolean>(true);

    public readonly destroy$ = destroy$();

    protected tooltipBalloonComponentRef: DynamicComponentRef<TooltipBalloonComponent>;

    protected readonly updateTooltipContent$ = new Subject<void>();

    protected dynamicTooltip: string | SafeHtml;

    protected isDynamicTooltipMode: boolean = false;

    constructor(
        protected elementRef: ElementRef,
        protected tooltipService: TooltipService,
        protected domSanitizer: DomSanitizer
    ) {}

    public ngOnInit(): void {
        const isEnabled$ = combineLatest([
            this.isEnabled$,
            this.createTooltipIsNotEmptyStream()
        ]).pipe(
            map(([isEnabled, isNotEmpty]) => !!(isEnabled && isNotEmpty)),
            distinctUntilChanged()
        );

        this.tooltipService.init(
            this.elementRef,
            isEnabled$,
            this.createTooltipContentStream()
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    public ngOnChanges(changes: TypedSimpleChanges<TooltipDirective>): void {
        if (changes.tooltip) {
            this.updateTooltipContent$.next();
        }
    }

    public hide(): void {
        if (this.tooltipBalloonComponentRef) {
            this.tooltipBalloonComponentRef.destroy();
        }
    }

    public setDynamicTooltip(isDynamicTooltipMode: boolean, dynamicTooltip?: string | SafeHtml): void {
        this.isDynamicTooltipMode = isDynamicTooltipMode;
        this.dynamicTooltip = this.isDynamicTooltipMode
            ? dynamicTooltip
            : undefined;

        this.updateTooltipContent$.next();
    }

    protected getTooltipContent(): string {
        const tooltipValue = this.isDynamicTooltipMode
            ? this.dynamicTooltip
            : this.tooltip;

        return this.domSanitizer.sanitize(SecurityContext.NONE, tooltipValue);
    }

    protected createTooltipContentStream(): Observable<string> {
        return this.updateTooltipContent$.pipe(
            startWith(undefined),
            map(() => this.getTooltipContent())
        );
    }

    private createTooltipIsNotEmptyStream(): Observable<boolean> {
        return defer(() => {
            let isNotEmpty: boolean = this.isTooltipNotEmpty();

            return this.updateTooltipContent$.pipe(
                map(() => this.isTooltipNotEmpty()),
                tap(newIsNotEmpty => isNotEmpty = newIsNotEmpty),
                share(),
                startWith(isNotEmpty)
            );
        });
    }

    private isTooltipNotEmpty(): boolean {
        return !!_.trim(this.getTooltipContent());
    }

}
